import React from 'react';
import ContentBanner from '@ajat/design-system/ContentBlocks/ContentBanner';
import { ComponentPage, DemoCard } from '../../shared';
import { HERO_IMAGE, DEMO_PRODUCTS } from './contentDemoData';

const VOGN_IMAGE = require('../../../../../assets/cls-dk/landing/vognturer.jpg');

const SLIDER_IMAGES = [
  { src: DEMO_PRODUCTS[0].image, alt: 'Package 1' },
  { src: DEMO_PRODUCTS[1].image, alt: 'Package 2' },
  { src: DEMO_PRODUCTS[2].image, alt: 'Package 3' },
];

export const meta = {
  component: ContentBanner,
  title: 'Content Banner',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/ContentBlocks/ContentBanner',
  figmaNode: '3874:669',
  description:
    'Two-column banner — a content block beside a visual — that stacks on mobile. The visual takes one image or an array, which turns it into a cross-fading slider. `direction` flips which side the content sits on, so alternating sections down a landing page need no extra CSS.',
  props: [
    ['visualPosition', "'end' | 'start'", "'end'", 'Desktop side of the visual — the DS name shared with ContentBannerV2 / HeroBannerV3'],
    ['direction', "'left' | 'right'", '—', "Deprecated — the old spelling ('right' = visual first = visualPosition=\"start\")"],
    ['align', "'left' | 'center' | 'right'", "'left'", 'Text alignment inside the content block'],
    ['theme', "'light' | 'dark'", "'light'", 'Dark inverts the text and icon colours'],
    ['showSubtitle', 'bool', 'true', 'Show the subtitle'],
    ['subtitle', 'string', "''", 'Eyebrow above the title (typo-h5)'],
    ['showTitle', 'bool', 'true', 'Show the title'],
    ['title', 'string', "''", 'Headline'],
    ['titleVariant', "'h1' | 'h2'", "'h2'", 'typo-h1-editorial or typo-h2-editorial'],
    ['titleAs', "'h1' | 'h2' | 'h3'", 'follows titleVariant', 'Heading element of the title — "h2" for an h1-sized banner below the page hero (one h1 per page)'],
    ['showBullets', 'bool', 'true', 'Show the bullet list'],
    ['bullets', 'array', '[]', '[{ title, description }] — each rendered with a Check icon'],
    ['showParagraph', 'bool', 'true', 'Show the paragraph'],
    ['paragraph', 'string', "''", 'Body copy (typo-body)'],
    ['showSlogan', 'bool', 'true', 'Show the slogan'],
    ['slogan', 'string', "''", 'Slogan line (typo-h4)'],
    ['showLink', 'bool', 'true', 'Show the link'],
    ['linkLabel', 'string', "''", 'Link label (Hyperlink editorial-large)'],
    ['linkHref', 'string', "'#'", 'Link target'],
    ['onLinkClick', 'func', '—', 'Link handler — preventDefault it when routing in JS'],
    ['showCaption', 'bool', 'true', 'Show the caption'],
    ['caption', 'string', "''", 'Small note (typo-body-sm)'],
    ['paddingTop', 'string', 'null', 'Override the section padding-top'],
    ['paddingBottom', 'string', 'null', 'Override the section padding-bottom'],
    ['contentBg', 'string', 'null', 'Background of the content column; falls back to gray-1 (light) or gray-10 (dark)'],
    ['image', 'string', 'null', 'Single visual'],
    ['imageAlt', 'string', "''", 'Alt text for the single visual'],
    ['images', 'array', 'null', '[{ src, alt }] — two or more turns the visual into a cross-fading slider'],
    ['sliderInterval', 'number', '5', 'Seconds between slides'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'On the CLS-DK landing',
      code: `import ContentBanner from '@ajat/design-system/ContentBlocks/ContentBanner';

<ContentBanner
  direction="right"
  align="center"
  subtitle="Klar til studenterkørslen"
  title="Bestil Vogntrøjer"
  titleVariant="h1"
  titleAs="h2"
  showBullets={false}
  showParagraph={false}
  showCaption={false}
  contentBg="#e5e6e4"
  slogan="Matchende trøjer til hele klassen, når I fejrer huen på vognen"
  linkLabel="Se vogntrøjer"
  onLinkClick={(e) => {
    e.preventDefault();
    onMenuItemClick('menu_2');
  }}
  image={vognImage}
  imageAlt="Vogntrøjer til studenterkørsel"
/>`,
    },
    {
      title: 'Dark, with a slider',
      code: `<ContentBanner
  theme="dark"
  align="center"
  contentBg="#192421"
  subtitle="Discounts up to 40%"
  title="Attraktive pakkeløsninger"
  titleVariant="h1"
  titleAs="h2"
  showBullets={false}
  showParagraph={false}
  slogan="Spar op til 20% på pakkeløsninger"
  linkLabel="Se mere"
  showCaption={false}
  images={[
    { src: banner1, alt: 'Package 1' },
    { src: banner2, alt: 'Package 2' },
    { src: banner3, alt: 'Package 3' },
  ]}
  sliderInterval={5}
/>`,
    },
  ],
};

export default function ContentBannerPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="As shipped on CLS-DK" hint='direction="right", centred, custom content ground' flush>
        <ContentBanner
          direction="right"
          align="center"
          subtitle="Klar til studenterkørslen"
          title="Bestil Vogntrøjer"
          titleVariant="h1"
          showBullets={false}
          showParagraph={false}
          contentBg="#e5e6e4"
          slogan="Matchende trøjer til hele klassen, når I fejrer huen på vognen"
          linkLabel="Se vogntrøjer"
          linkHref="#"
          showCaption={false}
          image={VOGN_IMAGE}
          imageAlt="Vogntrøjer til studenterkørsel"
        />
      </DemoCard>

      <DemoCard title="Dark with a slider" hint="images.length > 1 cross-fades every 5s" flush>
        <ContentBanner
          direction="left"
          align="center"
          theme="dark"
          contentBg="#192421"
          subtitle="Discounts up to 40%"
          title="Attraktive pakkeløsninger"
          titleVariant="h1"
          showBullets={false}
          showParagraph={false}
          slogan="Spar op til 20% på pakkeløsninger"
          linkLabel="Se mere"
          linkHref="#"
          showCaption={false}
          images={SLIDER_IMAGES}
          sliderInterval={5}
        />
      </DemoCard>

      <DemoCard title="Light with bullets" hint="left-aligned, the full content stack" flush>
        <ContentBanner
          direction="left"
          align="left"
          subtitle="HVORFOR OS"
          title="Kvalitet, der holder"
          bullets={[
            { title: 'Økologisk bomuld', description: '80 % organisk bomuld, 20 % genbrugspolyester.' },
            { title: 'Tryk der holder', description: 'Testet vask efter vask.' },
            { title: 'Levering til skolen', description: 'Gratis, samlet levering.' },
          ]}
          paragraph="Vi har leveret tøj til skoler og foreninger i over 20 år, med fokus på kvalitet og enkelhed for kunden."
          slogan="Mere end 75.000 kunder om året"
          linkLabel="Læs mere"
          linkHref="#"
          caption="Priser inkl. moms"
          image={HERO_IMAGE}
          imageAlt="Vores kollektion"
        />
      </DemoCard>

      <DemoCard title="Minimal" hint="title and link only" flush>
        <ContentBanner
          direction="right"
          align="center"
          showSubtitle={false}
          title="Design tøj til jeres studietur"
          showBullets={false}
          showParagraph={false}
          showSlogan={false}
          showCaption={false}
          linkLabel="Kom i gang"
          linkHref="#"
          image={HERO_IMAGE}
          imageAlt="Studietur"
        />
      </DemoCard>
    </ComponentPage>
  );
}
